import * as React from 'react'
import { useMemo } from 'react'
import { twMerge } from 'tailwind-merge'
import { Markdown, ResizableDiv } from '@highlight/ui'
import { Skeleton } from '@renderer/components/ui/skeleton.tsx'
import type { Components } from 'react-markdown'
import { useSummaryById, useSummaryDate } from '@renderer/chat/hooks'
import { useSummaryContext } from '../hooks/use-summary-context'
import { useSummaryEdit } from '../hooks/use-summary-edit'
import { useSummaryTaskToggle } from '../hooks/use-summary-task-toggle'
import { useSummaryTaskDelete } from '../hooks/use-summary-task-delete'
import { useSummaryQuickAdd } from '../hooks/use-summary-quick-add'
import { SummaryListMarkdown, SummaryInputMarkdown, ArtifactLinkMarkdown } from './summary-markdown-components'
import { stripArtifactLinks } from '../utils/clean-artifact-text'
import { SummaryEditor } from './summary-editor'
import { QuickAddInput } from './quick-add-input'

interface SummaryBodyProps extends Omit<
  React.ComponentProps<'div'>,
  'onAnimationStart' | 'onAnimationEnd' | 'onDragStart' | 'onDragEnd' | 'onDrag'
> {
  onClickListItem?: (item: any) => void
  hideQuickAdd?: boolean
  containerClassName?: string
}

export function SummaryBody({
  className,
  containerClassName,
  onClickListItem,
  hideQuickAdd,
  ...props
}: SummaryBodyProps) {
  const { summary, isExpanded } = useSummaryContext()
  const { data: fullSummary } = useSummaryById(summary.id)
  const { isToday } = useSummaryDate(summary)
  const { isEditing, edited, setEdited, onSaveEdits } = useSummaryEdit()
  const { onClickListItem: onClickTaskItem, isSaving: savingTasks } = useSummaryTaskToggle(
    edited,
    setEdited,
    onSaveEdits,
  )
  const { onDeleteListItem: onDeleteTaskItem, isSaving: savingDelete } = useSummaryTaskDelete(
    edited,
    setEdited,
    onSaveEdits,
  )
  const { addItemToSection, isSaving: savingQuickAdd } = useSummaryQuickAdd(edited, setEdited, onSaveEdits)

  const isSaving = savingTasks || savingQuickAdd || savingDelete
  const previewWordCount = isToday ? 400 : 180

  const content = useMemo(() => {
    if (!fullSummary) return undefined
    return edited ?? fullSummary.summary
  }, [fullSummary, edited])

  const processedContent = useMemo(() => {
    if (!content) {
      return null
    }

    const hasNewLinesOrMore = content.split('\n').length > 1 || content.length >= previewWordCount

    if (isExpanded || !hasNewLinesOrMore) {
      return content
    }

    // When collapsed, strip artifact markdown links before truncation
    // This prevents broken/partial links in the preview
    const firstLine = stripArtifactLinks(content.split('\n')[0])

    if (firstLine.length <= previewWordCount) {
      return `${firstLine}...**Read More**`
    }

    // Simple truncation since we've already removed artifact links
    const truncatedContent = firstLine.slice(0, previewWordCount).trim()
    return `${truncatedContent}... **Read More**`
  }, [isExpanded, content, previewWordCount])

  const markdownComponents = useMemo(() => {
    return {
      li: (props: React.PropsWithChildren<React.ComponentProps<'li'> & { node?: any }>) => {
        // Use the line number from the node's position - this is reliable
        const lineNumber = props.node?.position?.start?.line
        return (
          <SummaryListMarkdown
            {...(props as any)}
            onClick={onClickListItem ?? onClickTaskItem}
            data-line-number={lineNumber}
            disabled={isSaving}
          />
        )
      },
      input: (props: React.ComponentProps<'input'> & { node: any }) => {
        return (
          <SummaryInputMarkdown
            inputProps={props}
            node={props.node}
            onClick={onClickListItem ?? onClickTaskItem}
            onDelete={onDeleteTaskItem}
            disabled={isSaving}
          />
        )
      },
      a: (props: React.ComponentProps<'a'>) => {
        return <ArtifactLinkMarkdown {...props} />
      },
    } as Components
  }, [onClickListItem, onClickTaskItem, isSaving])

  const renderContent = useMemo(() => {
    if (!processedContent) return null

    // Convert markdown links with custom protocols to https://artifact.local format
    // This is necessary because react-markdown doesn't pass custom protocol URLs to link components
    let markdownWithArtifacts = processedContent.replace(
      /\[([^\]]+)\]\((CONVERSATION|AUDIO_NOTE|INSIGHT):\/\/([^)]+)\)/g,
      (match, text, type, id) => {
        return `[${text}](https://artifact.local/${type}/${id})`
      },
    )

    // External app deeplinks (Notion, Linear, Slack, etc.)
    // These need to be converted to pass through markdown URL sanitization
    markdownWithArtifacts = markdownWithArtifacts.replace(
      /\[([^\]]+)\]\((NOTION|LINEAR|SLACK|FIGMA|GITHUB|ASANA|JIRA|TRELLO):\/\/([^)]+)\)/gi,
      (_, text, type, path) => `[${text}](https://deeplink.local/${type.toUpperCase()}/${encodeURIComponent(path)})`,
    )

    // Convert bare checkbox syntax to proper GFM task lists
    // Handles escaped brackets (\[), HTML entities (&#x20;), and bare syntax
    // Transforms "[ ]", "\[ ]", "[&#x20;]" → "- [ ]" and "[x]", "\[x]" → "- [x]"
    markdownWithArtifacts = markdownWithArtifacts.replace(
      /^(\s*)\\?\[( |x|X|&#x20;)\]\\?\s*/gm,
      (match, indent, checkbox) => {
        const cleanCheckbox = checkbox === '&#x20;' ? ' ' : checkbox
        return `${indent}- [${cleanCheckbox}] `
      },
    )

    return (
      <Markdown size="sm" components={markdownComponents} className={twMerge(isExpanded && 'select-text')}>
        {markdownWithArtifacts}
      </Markdown>
    )
  }, [processedContent, markdownComponents, isExpanded])

  const resizableDivProps = props

  return (
    <ResizableDiv
      className={twMerge('text-md !w-full select-text overflow-hidden', className)}
      containerClassName={twMerge('h-fit w-full flex flex-col gap-2.5', containerClassName)}
      data-summary-body
      {...resizableDivProps}
    >
      {isEditing ? (
        <SummaryEditor />
      ) : processedContent ? (
        renderContent
      ) : (
        <div className="flex flex-col gap-1.5">
          <Skeleton className={'h-4 w-[75%]'} />
          <Skeleton className={'h-4 w-[60%]'} />
        </div>
      )}
      {isExpanded && !isEditing && processedContent && !hideQuickAdd && (
        <QuickAddInput sectionName="Follow up" onAdd={addItemToSection} />
      )}
    </ResizableDiv>
  )
}
